import { useEffect, useRef, useState } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
// openlayers
import { Overlay, getUid } from 'ol';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import View from 'ol/View';
import { containsCoordinate, createEmpty, extend, isEmpty } from 'ol/extent';
import { Circle as CircleGeom, LineString, Point } from 'ol/geom';
import Draw from 'ol/interaction/Draw';
import { Vector } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import { fromLonLat, transform } from 'ol/proj';
import Projection from 'ol/proj/Projection';
import { register } from 'ol/proj/proj4';
import { TileImage, Vector as VectorSource, XYZ } from 'ol/source';
import { getLength } from 'ol/sphere';
import { Circle, Fill, Icon, Stroke, Style, Text } from 'ol/style';
import TileGrid from 'ol/tilegrid/TileGrid';
import proj4 from 'proj4';

import SliderMap from 'components/modules/SliderMap';
import useStateRef from 'hooks/useStateRef';
import { useWebSocketMessages } from 'hooks/useWebSocketMessages';
import { LooknTellGetTermLocateByMdn, getViewerKey, onaviGetTermLocateByMdn } from 'services/api-service/center';
import { MomentService } from 'services/moment-service';
import { utilService } from 'services/util-service';
import { ActionCtor } from 'store/modules/main';
import { IStoreState } from '../../../store/modules';
import MapCoordinate from './MapCoordinate';
import MapDatePicker from './MapDatePicker';
import MapOverlay from './MapOverlay ';
import MapTimePicker from './MapTimePicker';
import ModalInfo from './ModalInfo';
import ModalResult from './ModalResult';
import ModalUpdate from './ModalUpdate';

interface MapProps {
  userinfo?: any;
  participants?: string;
  roomId?: string;
  message?: any;
  onRegisterCase: (
    date: string,
    time: string,
    lat: string,
    lng: string,
    loc: string,
    dtl: string,
    rslt: string | undefined,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    mdn: string,
    alimType: string
  ) => Promise<boolean> | void;
  onInitialize: () => void;
  onDisconnect: () => void;
  onMute: (muted: boolean) => void;
}

const CenterMap = ({
  userinfo,
  participants,
  roomId,
  message,
  onRegisterCase,
  onInitialize,
  onDisconnect,
  onMute,
}: MapProps) => {
  const dispatch = useDispatch();
  const [muted, setMuted] = useState(false);
  const [isOceanMap, setIsOceanMap] = useState(true);
  const measureRef = useRef(false);
  const [isMeasure, setIsMeasure] = useState(false);
  measureRef.current = isMeasure;
  const [map, setMap] = useState<Map>();
  const rescues = useSelector((store: IStoreState) => store.MainReducer.rescues);
  const locations = useSelector((store: IStoreState) => store.MainReducer.locations);
  const curRoom: any = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const vessels = useSelector((store: IStoreState) => store.MainReducer.vessels);
  const users = useSelector((store: IStoreState) => store.MainReducer.users);
  const userInfo = useSelector((store: IStoreState) => store.UserReducer.userInfo);
  const [roomInfo, setRoomInfo, roomInfoRef] = useStateRef({
    isOpen: false,
    isUpdate: false,
    isClose: false,
    date: MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD'),
    time: MomentService.getInstance().getDateFormat(new Date(), 'HH:mm'),
    lat: '0',
    lng: '0',
    loc: '',
    dtl: '',
    prcRslt: '',
    mdn: '',
    isExpire: true,
    dLoc: '확인중',
    wCond: '확인중',
    accType: '확인중',
    dType: '확인중',
    dRoute: '해양경찰',
    alimType: '',
  });
  const toastMessage = (message: string) => <div style={{ fontSize: '1.1rem' }}>{message}</div>;
  const mapElement = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map>();
  mapRef.current = map;

  const key = '0AE3197F-87CA-3304-8A9A-74CA8BB0E631';

  // Connenct Websocket
  const { messages, boats } = useWebSocketMessages(vessels, roomId, curRoom, roomInfo, {
    onError: (error) => {
      console.error('WebSocket 오류:', error);
    },
    reconnectDelay: 5000,
    enableDebug: true,
  });

  const OceanBaseMap = new TileLayer({
    className: 'OceanBaseMap',
    visible: true,
    // type: 'base',
    extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
    minResolution: 1.9087865126953125,
    maxResolution: 1954.597389 + 250,
    // zIndex: 100,
    source: new XYZ({
      projection: new Projection({
        code: 'EPSG:5179',
        extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
        units: 'm',
      }),
      tileSize: 256,
      minZoom: 1,
      cacheSize: 10000,
      tileGrid: new TileGrid({
        extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
        origin: [-200000.0, -28024123.62],
        resolutions: [
          1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
          15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
        ],
      }),
      tileUrlFunction: (tileCoord) => {
        let path = 'http://www.khoa.go.kr/safety_map/basemap.do?mapType=DL';
        const z = tileCoord[0] + 6;
        const x = Math.abs(tileCoord[1]);
        const y = Math.abs(tileCoord[2]) - 1;
        if (z < 10) {
          path += `&z=L0${z}&y=${y}&x=${x}`;
        } else {
          path += `&z=L${z}&y=${y}&x=${x}`;
        }
        return path;
      },
    }),
  });
  const OceanBaseView = new View({
    projection: new Projection({
      code: 'EPSG:5179',
      extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
      units: 'm',
    }),
    extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
    enableRotation: false,
    resolutions: [
      1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
      15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
    ],
    maxResolution: 1954.597389,
    zoomFactor: 1,
    center: [956498.5710969 + 70000, 1819967.0629328 + 20000],
    minZoom: 1,
    maxZoom: 10,
    zoom: 3,
  });
  const tms_resolution = [
    1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
    15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
  ];
  const tms_extent = [531371.84, 967246.47, 1576674.68, 2274021.31];
  const m_extent = [-200000.0, -28024123.62, 31824123.62, 4000000.0];
  const org_extent = [37953.1466, 1126946.7253, 2152527.8074, 2873292.704];
  const projection = new Projection({
    code: 'EPSG:5179',
    extent: tms_extent,
  });
  const tileGrid = new TileGrid({
    extent: m_extent,
    origin: [m_extent[0], m_extent[1]],
    resolutions: tms_resolution,
  });
  const ENC57BaseMap = new TileLayer({
    className: 'ENC57BaseMap',
    visible: true,
    source: new TileImage({
      projection: projection,
      // tileSize: 256,
      tileGrid: tileGrid,
      tileUrlFunction: function (tileCoord) {
        const zoom = tileCoord[0] + 6;
        let zero = '';
        if (10 > zoom) {
          zero += '0';
        }
        const z = zero + zoom;
        const x = Math.abs(tileCoord[1]);
        const y = Math.abs(tileCoord[2]) - 1;
        return 'http://navada.kr/basemap/ENC/L' + z + '/' + x + '/' + y + '.png';
        // return 'http:///210.104.116.161/basemap/ENC/L' + z + '/' + x + '/' + y + '.png';
      },
    }),
  });
  const ENC57BaseView = new View({
    projection: projection,
    // restrictedExtent: org_extent,
    maxResolution: 1377.2986945,
    center: [956498.5710969, 1819967.0629328],
    zoom: 1,
    minZoom: 0,
    maxZoom: 12,
  });
  const vworldMap = new TileLayer({
    className: 'vworld_Base',
    visible: false,
    source: new XYZ({
      url: 'https://api.vworld.kr/req/wmts/1.0.0/' + key + '/Base/{z}/{y}/{x}.png',
    }),
  });
  const vworldView = new View({
    zoom: 9,
    minZoom: 0,
    maxZoom: 21,
    center: transform([127.7, 36.7], 'EPSG:4326', 'EPSG:900913'),
    constrainResolution: true,
    enableRotation: false,
  });

  const onChangeDate = (value: string) => {
    // console.log('onChangeDate  ', roomInfo, value);
    setRoomInfo((prevState) => ({
      ...prevState,
      date: value,
    }));
  };

  const onChangeTime = (value: string) => {
    // console.log('onChangeTime  ', roomInfo, value);
    setRoomInfo((prevState) => ({
      ...prevState,
      time: value,
    }));
  };

  const clearPosition = () => {
    // 1. Clear Old Position
    if (mapRef.current !== undefined) {
      prevLength.current = 0;
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (layer && layer.getClassName() === 'vessel') {
            if (mapRef.current !== undefined) {
              layer
                .getSource()
                .getFeatures()
                .forEach((feature: any) => {
                  layer.getSource().removeFeature(feature);
                });
              mapRef.current.removeLayer(layer);
            }
          }
          if (mapRef.current !== undefined && layer && layer.getClassName() === 'Point_Layer') {
            mapRef.current.removeLayer(layer);
          }
        });
    }
  };

  const onChangePosition = (lng: number, lat: number, date: string, mdn: string) => {
    // 1. Clear Old Position
    clearPosition();
    // 2. Update Info
    setRoomInfo((prevState) => ({
      ...prevState,
      lat: lat.toString(),
      lng: lng.toString(),
      mdn: mdn,
    }));
    // 3. Check latitude & longitude
    if (lng.toString() === '' || lat.toString() === '' || lng.toString() === '0' || lat.toString() === '0') {
      return toast(toastMessage('GPS 정보가 없습니다.'), { type: 'error' });
    }
    // 4. Update New Position
    displayPoint(lng, lat, date, true);
  };

  useEffect(() => {
    proj4.defs(
      'EPSG:5179',
      '+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +units=m +no_defs'
    );
    register(proj4);

    const initialMap = new Map({
      target: mapElement.current!,
      layers: [ENC57BaseMap, vworldMap, drawVector],
      view: ENC57BaseView,
      controls: [],
    });
    initialMap.on('click', handleMapClick);
    initialMap.on('dblclick', handleMapDbClick);
    initialMap.on('pointermove', displayTooltip);
    setMap(initialMap);
    createTootip();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (roomId !== undefined || curRoom !== undefined) {
          return;
        }
        clearPosition();
        setRoomInfo((prevState) => ({
          ...prevState,
          lat: '0',
          lng: '0',
          mdn: '',
        }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [roomId, curRoom]);

  useEffect(() => {
    prevLength.current = 0;
    ClearPoint();
    if (roomId === undefined) {
      setRoomInfo((prevState) => ({
        ...prevState,
        date: MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD'),
        time: MomentService.getInstance().getDateFormat(new Date(), 'HH:mm'),
        lat: '0',
        lng: '0',
        loc: '',
        dtl: '',
        prcRslt: '',
        mdn: '',
        isExpire: true,
        dLoc: '확인중',
        wCond: '확인중',
        accType: '확인중',
        dType: '확인중',
        dRoute: '해양경찰',
        alimType: '',
      }));
    }
  }, [roomId]);

  useEffect(() => {
    if (curRoom !== undefined && curRoom.isExpire) {
      setRoutePositiion(locations);
      if (locations !== undefined && locations.length > 0) {
        // Merge GPS Array
        const merged =
          locations.length === 1
            ? locations[0].loc
            : locations.reduce((prev, cur) => {
                if (prev !== undefined && prev.length > 0 && cur !== undefined && cur.loc.length > 0) {
                  return prev.concat(cur.loc);
                }
                return cur.loc;
              }, []);
        // Sort by Time
        const sortedLoc = merged.sort((a, b) => (a.saveTime > b.saveTime ? 1 : -1));
        setPlayData(sortedLoc);
      } else {
        setPlayData(undefined);
      }
      // setPlayData(locations);
      setIsPlaying(true);
    } else {
      createDrawLayer(locations);
    }
  }, [locations]);

  useEffect(() => {
    if (message !== undefined) {
      const location = JSON.parse(decodeURIComponent(message.text));
      // console.log('CenterMap message', message.from.name(), location.role, location);
      appendLocation(message.from.name(), location.role, location);
    }
  }, [message]);

  const prevLength = useRef(0);

  useEffect(() => {
    if (messages.length === 0) {
      prevLength.current = 0;
      return;
    }
    if (prevLength.current === 0 && messages.length > 0) {
      drawVesselLayer(roomInfo.mdn, messages);
    } else if (messages.length > prevLength.current) {
      const newMessages = messages.slice(prevLength.current);
      appendVessel(roomInfo.mdn, newMessages[0]);
    }
    prevLength.current = messages.length;
  }, [messages]);

  useEffect(() => {
    if (boats.length === 0) {
      return;
    }
    if (boats.length > 0) {
      drawBoatsLayer(roomInfo.mdn, boats);
    }
  }, [boats]);

  useEffect(() => {
    // console.log('CenterMap curRoom', curRoom);
    if (curRoom !== undefined && curRoom.lat !== null && curRoom.lng !== null) {
      displayPoint(curRoom.lng, curRoom.lat, curRoom.accDate + ' ' + curRoom.accTime, true);
      setRoomInfo((prevState) => ({
        ...prevState,
        date: curRoom.accDate,
        time: curRoom.accTime,
        lat: curRoom.lat,
        lng: curRoom.lng,
        loc: curRoom.accLoc,
        dtl: curRoom.accDtl,
        prcRslt: curRoom.prcRslt,
        dLoc: curRoom.dLoc,
        wCond: curRoom.wCond,
        accType: curRoom.accType,
        dType: curRoom.dType,
        dRoute: curRoom.dRoute,
        mdn: curRoom.mdn || '',
        isExpire: curRoom.isExpire === undefined ? true : curRoom.isExpire,
        alimType: curRoom.alimType,
      }));
      if (curRoom.mdn !== '' && curRoom.mdn !== null) {
        // Call Boarding Info
        const targetVessel = vessels.find((v) => v.mdn === curRoom.mdn);
        if (!curRoom.isExpire && targetVessel) dispatch(ActionCtor.getPassenger([targetVessel.bbc, curRoom.accDate]));
      }
      if (curRoom.dType !== '' && curRoom.dType === '실종자 수색') {
        getOnavi(curRoom);
      }
    }
  }, [curRoom]);

  const getOnavi = async (curInfo: any) => {
    const regex = /마지막 위치수신 시간\s*:\s*(.*)/;
    const match = curInfo.accDtl.match(regex);
    const lastReceivedTime = match ? match[1].trim() : '';

    try {
      const mdn = curInfo.mdn || '';
      const searchDateTime = lastReceivedTime || '';
      const term = '10';

      let response: any = null;

      if (curInfo?.alimType === 'rescue') {
        const params = {
          mdn,
          searchDateTime,
          term,
          searchKeyword: '',
          radius: '50',
        };
        const form = new FormData();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            form.append(key, String(value));
          }
        });
        response = await onaviGetTermLocateByMdn(form);
      } else {
        const params = {
          mdn,
          searchDateTime,
          term,
        };
        const form = new FormData();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            form.append(key, String(value));
          }
        });
        response = await LooknTellGetTermLocateByMdn(form);
      }

      const logs = response?.data?.log;
      if (Array.isArray(logs) && logs.length > 1) {
        drawVesselLayer(curInfo.mdn, logs);
      }
    } catch (error) {
      console.error('Onavi 경로 정보 조회 실패:', error);
    }
  };

  const changeMap = () => {
    // console.log('changeMap', isOceanMap);
    if (mapRef.current !== undefined) {
      mapRef.current.getLayers().forEach(function (layer) {
        if (isOceanMap) {
          if (layer.getClassName() == 'vworld_Base') {
            layer.setVisible(true);
          }
          if (layer.getClassName() == 'ENC57BaseMap') {
            layer.setVisible(false);
          }
        } else {
          if (layer.getClassName() == 'vworld_Base') {
            layer.setVisible(false);
          }
          if (layer.getClassName() == 'ENC57BaseMap') {
            layer.setVisible(true);
          }
        }
      });
      setIsOceanMap((prevIsRunning) => !prevIsRunning);
    }
  };

  const setMeasure = () => {
    // console.log('setMeasure', isMeasure);
    if (isMeasure) {
      if (mapRef.current !== undefined) {
        mapRef.current.removeInteraction(mapDraw.current!);
      }
      setIsMeasure(false);
    } else {
      setIsMeasure(true);
      addInteraction();
    }
  };

  const setPosition = () => {
    if (curRoom) {
      dispatch(ActionCtor.getRoom(roomId));
    }
  };

  // map click handler
  const handleMapClick = (event) => {
    // console.log('handleMapClick', event);
    if (mapRef.current !== undefined) {
      const overlays = [...mapRef.current.getOverlays().getArray()];
      overlays.forEach((overlay) => {
        if (!overlay.getId()?.toString().includes('Measure_')) {
          overlay.setMap(null);
        }
      });
      mapRef.current.forEachFeatureAtPixel(
        event.pixel,
        function (feature, layer) {
          if (mapRef.current !== undefined) {
            const overlays = [...mapRef.current.getOverlays().getArray()];
            overlays.some((overlay) => {
              if (feature !== undefined && feature.get('type') === 'position') {
                if (
                  overlay.getId() === 'Overlay_' + feature.get('data').phone ||
                  overlay.getId() === 'route_overlay_' + feature.get('data').phone
                ) {
                  if (mapRef.current !== undefined) {
                    overlay.setMap(mapRef.current);
                    return true;
                  }
                }
              }
            });
          }
        },
        {
          hitTolerance: 10,
        }
      );
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handleMapDbClick = (event) => {
    if (measureRef.current) {
      event.preventDefault();
      event.stopPropagation();
      setIsMeasure(false);
      return;
    }
    if (mapRef.current !== undefined) {
      // 1. Clear Old Position
      clearPosition();
      // 2. Set New Click Position
      const clickedCoord = mapRef.current.getCoordinateFromPixel(event.pixel);
      const transormedCoord = transform(clickedCoord, 'EPSG:5179', 'EPSG:4326');
      if (roomInfoRef.current.lng === '0' && roomInfoRef.current.lng === '0') {
        displayPoint(
          transormedCoord[0],
          transormedCoord[1],
          MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD HH:mm:ss')
        );
        setRoomInfo((prevState) => ({
          ...prevState,
          lng: transormedCoord[0].toString(),
          lat: transormedCoord[1].toString(),
          mdn: '',
        }));
      } else {
        // Ask if you want to change it
        displayPoint(
          transormedCoord[0],
          transormedCoord[1],
          MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD HH:mm:ss')
        );
        setRoomInfo((prevState) => ({
          ...prevState,
          lng: transormedCoord[0].toString(),
          lat: transormedCoord[1].toString(),
          mdn: '',
        }));
      }
      // console.log('handleMapDbClick', transormedCoord);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const displayTooltip = (evt: any) => {
    if (mapRef.current !== undefined) {
      const feature = mapRef.current.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
        if (layer && !layer.getClassName().includes('Base')) {
          return feature;
        }
      });

      if (feature) {
        const properties = feature.getProperties();
        const id = properties.id;
        if (id === 'point' || id === 'start') {
          mapRef.current.addOverlay(tooltip);
          const data = feature.get('data');
          if (data === undefined) {
            tooltipElement.innerHTML = '';
          } else {
            tooltipElement.innerHTML =
              `일시 : ${data.dataTime}` +
              `<br />` +
              `위치 :  ${utilService.convertToDms(data.lat, false)} ${utilService.convertToDms(data.lng, true)}` +
              `<br />`;
          }
          tooltip.setPosition(evt.coordinate);
        } else if (id === 'boat') {
          mapRef.current.addOverlay(tooltip);
          const data = feature.get('data');
          if (data === undefined) {
            tooltipElement.innerHTML = '';
          } else {
            tooltipElement.innerHTML =
              `선박 : ${data.name} / ${data.mdn.replace(/^012/, '010')}` +
              `<br />` +
              `일시 : ${data.dataTime}` +
              `<br />` +
              `위치 :  ${utilService.convertToDms(data.lat, false)} ${utilService.convertToDms(data.lng, true)}` +
              `<br />`;
          }
          tooltip.setPosition(evt.coordinate);
        } else {
          mapRef.current.removeOverlay(tooltip);
        }
      } else {
        mapRef.current.removeOverlay(tooltip);
      }
    }
  };
  // Display Overlay Tooltip
  let tooltipElement: any;
  let tooltip: any;
  const createTootip = () => {
    if (tooltipElement) {
      tooltipElement.parentNode.removeChild(tooltipElement);
    }
    tooltipElement = document.createElement('tooltip');
    tooltipElement.className = 'ol-tooltip';
    tooltip = new Overlay({
      element: tooltipElement,
      positioning: 'bottom-left',
      offset: [10, 0],
    });
  };

  const displayPoint = (lng: number, lat: number, date: string, moveView = false) => {
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (mapRef.current !== undefined && layer && layer.getClassName() === 'Point_Layer') {
            mapRef.current.removeLayer(layer);
          }
        });
    }

    // 1. Create Layer
    const vectorLayer = new Vector({
      className: 'Point_Layer',
      source: new VectorSource(),
      zIndex: 1000,
    });

    // 2. Add Mark & Point
    const iconStyle = [
      new Style({
        image: new Icon({
          src: require('../../../assets/images/icon_point.png'),
          // anchor: [0.5, 1.0],
        }),
        text: new Text({
          font: '12px Calibri,sans-serif',
          fill: new Fill({ color: 'red' }),
          stroke: new Stroke({
            color: 'red',
            width: 1,
          }),
          text: '사고위치',
          offsetY: 25,
        }),
      }),
    ];
    const features = new Feature({
      geometry: new Point(fromLonLat([lng, lat], 'EPSG:5179')),
      id: 'start',
      data: { lat, lng, dataTime: date },
    });
    features.setStyle(iconStyle);
    if (vectorLayer.getSource() !== null) {
      vectorLayer.getSource()?.addFeature(features);
    }

    // 3. Draw Circle with 100m interval - with zoom level dependency
    const circleFeatures: Feature[] = [];
    for (let i = 1; i <= 5; i++) {
      const circle = new Feature({
        geometry: new CircleGeom(fromLonLat([lng, lat], 'EPSG:5179'), i * 2000),
      });
      circle.setStyle(
        new Style({
          stroke: new Stroke({
            color: 'rgba(255, 0, 0, 0.8)',
            width: 2,
          }),
        })
      );
      if (vectorLayer.getSource() !== null) {
        vectorLayer.getSource()?.addFeature(circle);
      }
      circleFeatures.push(circle);
    }

    // 4. Add Layer
    if (mapRef.current !== undefined) {
      mapRef.current.addLayer(vectorLayer);
      // 5. Add zoom change event listener to show/hide circles based on zoom level
      const updateCircleVisibility = () => {
        if (mapRef.current) {
          const currentZoom = mapRef.current.getView().getZoom() || 0;
          circleFeatures.forEach((circle: Feature) => {
            // Show circles only at certain zoom levels
            if (currentZoom >= 3) {
              circle.setStyle(
                new Style({
                  stroke: new Stroke({
                    color: 'rgba(255, 0, 0, 0.8)',
                    width: 2,
                  }),
                })
              );
            } else {
              // Hide circles by setting style to null
              circle.setStyle(
                new Style({
                  stroke: new Stroke({
                    color: 'rgba(255, 0, 0, 0)',
                    width: 0,
                  }),
                })
              );
            }
          });
        }
      };
      // Initial visibility setup
      updateCircleVisibility();
      // Add listener for zoom changes
      mapRef.current.getView().on('change:resolution', updateCircleVisibility);

      if (moveView) {
        const view = mapRef.current.getView();
        const coord = fromLonLat([lng, lat], 'EPSG:5179');
        const extent = view.calculateExtent(mapRef.current.getSize());
        const zoom = view.getZoom() || 0;
        if (!containsCoordinate(extent, coord) || zoom < 3) {
          view.animate({ center: coord, zoom: Math.max(zoom, 3), duration: 400 });
        }
      }
    }
  };

  const ClearPoint = () => {
    if (mapRef.current !== undefined) {
      const overlays = [...mapRef.current!.getOverlays().getArray()];
      overlays.forEach((overlay) => {
        mapRef.current?.removeOverlay(overlay);
      });
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (mapRef.current !== undefined && !layer.getClassName().includes('Base')) {
            mapRef.current.removeLayer(layer);
          }
        });
      mapRef.current.getView().setCenter([956498.5710969, 1819967.0629328]);
      mapRef.current.getView().setZoom(1);
    }
  };

  const checkExist = (phone: string) => {
    let returnValue = false;
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === phone) {
            returnValue = true;
          }
        });
      return returnValue;
    }
  };

  const findNode = (nodes: any, targetValue: string, valueProperty = 'value') => {
    for (const node of nodes) {
      if (node[valueProperty] === targetValue) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const result = findNode(node.children, targetValue, valueProperty);
        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  const createDrawLayer = (location: any[]) => {
    // console.log('createDrawLayer ', location, mapRef.current?.getLayers());
    for (let j = 0; j < location.length; j++) {
      if (checkExist(location[j].phone)) {
        continue;
      }
      const rescue = findNode(rescues, location[j].phone);
      const coords = fromLonLat([location[j].loc[0].lng, location[j].loc[0].lat], 'EPSG:5179');

      const startIconFeature = new Feature({
        type: 'startIcon',
        geometry: new Point(coords),
      });

      const gpxStartIconStyle = [
        new Style({
          image: new Icon({
            // anchor: [0.5, 0.8],
            opacity: 1,
            scale: 0.6,
            src:
              location[j].role === 'V'
                ? require('../../../assets/images/icon_location_v.png')
                : require('../../../assets/images/icon_location_r.png'),
          }),
          text: new Text({
            font: '12px Calibri,sans-serif',
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({
              color: location[j].role === 'V' ? '#e63528' : '#0086ff',
              width: 2,
            }),
            text: location[j].role === 'V' ? '요구조자' : rescue === null ? '구조대' : rescue.label,
            offsetY: 12,
          }),
        }),
      ];

      const gpxStyles = {
        Point: new Style({
          image: new Circle({
            fill: new Fill({
              color: 'rgba(20, 140, 245, 0)',
            }),
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(20, 140, 245, 0)',
              width: 2,
            }),
          }),
        }),
        LineString: new Style({
          stroke: new Stroke({
            color: location[j].role === 'V' ? 'rgba(255, 0, 0, 1)' : 'rgba(54, 91, 242, 1)',
            width: 3,
          }),
        }),
      };

      const gpxDrawLayer = new Vector({
        className: location[j].phone,
        source: new VectorSource({ features: [] }),
        style: (feature: any) => {
          if (feature.get('type') === 'startIcon') {
            return gpxStartIconStyle;
          } else {
            return gpxStyles[feature.getGeometry().getType()];
          }
        },
        zIndex: 2,
      });

      if (gpxDrawLayer !== null) {
        gpxDrawLayer.getSource()?.addFeature(new Feature({ name: 'line', geometry: new LineString(coords, 'XY') }));
        gpxDrawLayer.getSource()?.addFeature(startIconFeature);
      }

      if (mapRef.current !== undefined) {
        mapRef.current
          .getLayers()
          .getArray()
          .slice()
          .forEach((layer) => {
            if (layer && layer.getClassName() === location[j].phone) {
              if (mapRef.current !== undefined) {
                mapRef.current.removeLayer(layer);
              }
            }
          });
        mapRef.current.addLayer(gpxDrawLayer);
      }

      if (location[j].loc.length > 1) {
        let coords = [] as any;
        for (let i = 0; i < location[j].loc.length; i++) {
          coords = coords.concat(fromLonLat([location[j].loc[i].lng, location[j].loc[i].lat], 'EPSG:5179'));
        }
        if (gpxDrawLayer !== null) {
          // const geometry: Geometry | undefined = gpxDrawLayer.getSource()?.getFeatureById('line')?.getGeometry();
          // if (geometry instanceof LineString) {
          //   console.log('appendLocation LineString', coords);
          //   geometry.appendCoordinate(coords);
          // }
          gpxDrawLayer.getSource()?.forEachFeature(function (feature: any) {
            if (feature !== undefined && feature.get('name') === 'line') {
              feature.getGeometry().appendCoordinate(coords);
            }
          });
        }
      }
      appendLocation(location[j].phone, location[j].role, location[j].loc[location[j].loc.length - 1]);
      if (mapRef.current !== undefined) {
        const extent = createEmpty();
        mapRef.current
          .getLayers()
          .getArray()
          .slice()
          .forEach((layer: any) => {
            if (
              mapRef.current !== undefined &&
              !layer.getClassName().includes('Base') &&
              !layer.getClassName().includes('Predic_')
            ) {
              const source = layer.getSource();
              extend(extent, source.getExtent());
            }
          });
        if (!isEmpty(extent)) {
          mapRef.current.getView().cancelAnimations();
          mapRef.current.getView().fit(extent, {
            size: mapRef.current.getSize(),
            padding: [200, 100, 200, 100],
            maxZoom: 7,
            duration: 2000,
          });
        }
      }
    }
  };

  const appendLocation = (phone: string, role: string, location: any) => {
    // console.log('appendLocation', phone, role, location);
    const coords = fromLonLat([location.lng, location.lat], 'EPSG:5179');
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (layer !== undefined && layer.getClassName() === phone) {
            layer.getSource().forEachFeature(function (feature) {
              if (feature !== undefined && feature.get('name') === 'line') {
                feature.getGeometry().appendCoordinate(coords);
                addLocationOverlay(feature, phone, role, location);
              }
            });
          }
        });
      drawPathPredic(phone, role, location.lat, location.lng, location.speed, location.heading);
    }
  };

  const drawPathPredic = (phone: string, role: string, lat: number, lng: number, spd: number, heading: number) => {
    // console.log('drawPathPredic', phone, role);
    const move_meter = spd * 60 * 5; //5분 예상 이동 거리(m 단위)
    const length_degree = 0.00001 * move_meter; //약 100m
    const direction_degree = heading;
    const lat_pred = lat + length_degree * Math.cos((direction_degree / 180) * Math.PI);
    const lng_pred = lng + length_degree * Math.sin((direction_degree / 180) * Math.PI);
    const source = new VectorSource();
    source.addFeature(
      new Feature({
        geometry: new LineString(fromLonLat([lng, lat]).concat(fromLonLat([lng_pred, lat_pred])), 'XY'),
        name: 'Predic_' + phone,
      })
    );
    const predicLayer = new Vector({
      className: 'Predic_' + phone,
      source,
      style: new Style({
        stroke: new Stroke({
          color: role === 'V' ? '#e63528' : '#0086ff',
          lineDash: [4, 8],
          width: 2,
        }),
      }),
    });
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === 'Predic_' + phone) {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(predicLayer);
    }
  };

  const addLocationOverlay = (feature: any, phone: string, role: string, location: any) => {
    // console.log('addLocationOverlay', phone, role, location, locations);
    if (location !== undefined && location.lng !== 0 && location.lat !== 0) {
      if (mapRef.current !== undefined && feature) {
        // 2. Check Overlay
        const overlay = mapRef.current.getOverlayById('Overlay_' + phone);
        if (overlay === null) {
          // 3. Create Feature
          mapRef.current
            .getLayers()
            .getArray()
            .filter((layer: any) => layer.getClassName() === phone)
            .forEach((layer: any) => {
              if (mapRef.current !== undefined && layer && layer.getClassName() === phone) {
                const feature = new Feature({
                  id: 'position',
                  type: 'position',
                  data: { phone, location },
                  geometry: new Point(fromLonLat([location.lng, location.lat], 'EPSG:5179')),
                });
                feature.setStyle(
                  getFeatureStyle({
                    role: role,
                    speed: location.speed,
                    heading: location.heading,
                  })
                );
                layer.getSource().addFeature(feature);
              }
            });
          // 4. Add Overlay
          const container = document.createElement('div');
          container.innerHTML = ReactDOMServer.renderToStaticMarkup(
            <MapOverlay users={users} phone={phone} role={role} location={location} locations={locations} />
          );
          const overlay = new Overlay({
            id: 'Overlay_' + phone,
            element: container,
            position: fromLonLat([location.lng, location.lat], 'EPSG:5179'),
            positioning: 'bottom-center',
            offset: [0, -40],
          });
          container.addEventListener('click', function (e: Event) {
            const target = e.target as HTMLDivElement;
            if (target.className.includes('overlay-close')) {
              onCloseOverlay(phone);
            } else if (target.className == 'overlay-cctv') {
              // console.log('Overlay cctv : ', target.className);
            }
          });
          mapRef.current.addOverlay(overlay);
          overlay.setMap(null);
        } else {
          // 5. Update Feature
          mapRef.current
            .getLayers()
            .getArray()
            .filter((layer: any) => layer.getClassName() === phone)
            .forEach((layer: any) => {
              if (mapRef.current !== undefined && layer && layer.getClassName() === phone) {
                const features = layer.getSource().getFeatures();
                if (features != null && features.length > 0) {
                  for (const x in features) {
                    const properties = features[x].getProperties();
                    const id = properties.id;
                    if (id == 'position') {
                      features[x].set('data', { phone, location });
                      features[x].setGeometry(new Point(fromLonLat([location.lng, location.lat], 'EPSG:5179')));
                      break;
                    }
                  }
                }
              }
            });
          // 6. Update Overlay
          const element = overlay.getElement();
          if (element !== undefined) {
            element.innerHTML = ReactDOMServer.renderToStaticMarkup(
              <MapOverlay users={users} phone={phone} role={role} location={location} locations={locations} />
            );
            overlay.setElement(element);
          }
          overlay.setPosition(fromLonLat([location.lng, location.lat], 'EPSG:5179'));
        }
      }
    }
  };

  const drawVesselLayer = (phone: string, location: any[]) => {
    if (!location || location.length === 0) return;

    const coords = location.map((loc) => fromLonLat([loc.lng, loc.lat], 'EPSG:5179'));

    const gpxStyles = {
      Point: new Style({
        image: new Circle({
          fill: new Fill({ color: 'rgba(20, 140, 245, 0.8)' }),
          radius: 6,
          stroke: new Stroke({ color: 'rgba(255,255,255,1)', width: 2 }),
        }),
        zIndex: 10,
      }),
      LineString: new Style({
        stroke: new Stroke({ color: 'rgba(54, 91, 242, 1)', width: 3 }),
        zIndex: 5,
      }),
    };

    const gpxDrawLayer = new Vector({
      className: 'vessel',
      source: new VectorSource({ features: [] }),
      style: (feature: any) => gpxStyles[feature.getGeometry().getType()],
      zIndex: 100,
    });

    const lineFeature = new Feature({
      name: 'line',
      geometry: new LineString(coords),
    });
    gpxDrawLayer.getSource()?.addFeature(lineFeature);

    console.log('drawVesselLayer', location);
    coords.forEach((coord, idx) => {
      const pointFeature = new Feature({
        name: `point${idx}`,
        id: 'point',
        geometry: new Point(coord),
        data: location[idx],
      });
      gpxDrawLayer.getSource()?.addFeature(pointFeature);
    });

    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === phone) {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(gpxDrawLayer);

      mapRef.current.getView().setCenter(coords[location.length - 1]);
    }

    appendVessel(phone, location[location.length - 1]);
    if (mapRef.current !== undefined) {
      const extent = createEmpty();
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (
            mapRef.current !== undefined &&
            !layer.getClassName().includes('Base') &&
            !layer.getClassName().includes('Predic_')
          ) {
            const source = layer.getSource();
            extend(extent, source.getExtent());
          }
        });
      if (!isEmpty(extent)) {
        mapRef.current.getView().cancelAnimations();
        mapRef.current.getView().fit(extent, {
          size: mapRef.current.getSize(),
          padding: [200, 100, 200, 100],
          maxZoom: 7,
          duration: 2000,
        });
      }
    }
  };

  const drawBoatsLayer = (phone: string, location: any[]) => {
    if (!location || location.length === 0) return;

    const coords = location
      .filter((loc) => loc.idx === 283 || loc.idx === 280)
      .map((loc) => fromLonLat([loc.lng, loc.lat], 'EPSG:5179'));

    const gpxStyles = {
      Point: new Style({
        image: new Icon({
          // anchor: [0.5, 0.8],
          opacity: 1,
          scale: 0.6,
          src: require('../../../assets/images/icon_location_v.png'),
        }),
        zIndex: 10,
      }),
    };

    const gpxDrawLayer = new Vector({
      className: 'boats',
      source: new VectorSource({ features: [] }),
      style: (feature: any) => gpxStyles[feature.getGeometry().getType()],
      zIndex: 100,
    });

    coords.forEach((coord, idx) => {
      const pointFeature = new Feature({
        name: `boat${idx}`,
        id: 'boat',
        geometry: new Point(coord),
        data: location[idx],
      });
      gpxDrawLayer.getSource()?.addFeature(pointFeature);
    });

    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === 'boats') {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(gpxDrawLayer);
    }
  };

  const appendVessel = (phone: string, location: any) => {
    const coords = fromLonLat([location.lng, location.lat], 'EPSG:5179');

    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (layer !== undefined && layer.getClassName() === 'vessel') {
            const source = layer.getSource();

            source.forEachFeature(function (feature) {
              if (feature !== undefined && feature.get('name') === 'line') {
                feature.getGeometry().appendCoordinate(coords);
                feature.changed();
              }
            });

            const pointFeature = new Feature({
              name: `point${Date.now()}`,
              id: 'point',
              geometry: new Point(coords),
              data: location,
            });

            source.addFeature(pointFeature);
          }
        });
    }
  };

  const onCloseOverlay = (phone: string) => {
    if (mapRef.current !== undefined) {
      mapRef.current
        .getOverlays()
        .getArray()
        .filter((layer: any) => layer.getClassName() === phone)
        .forEach((overlay) => {
          if (mapRef.current !== undefined) {
            overlay.setMap(null);
            // mapRef.current.removeOverlay(overlay);
          }
        });
    }
  };

  const updateCase = () => {
    // Check Expire
    if (curRoom !== undefined && curRoom.isExpire) {
      return toast(toastMessage('완료 처리된 사고입니다.'), { type: 'error' });
    }
    // Check latitude & longitude
    if (roomInfo.lng === '' || roomInfo.lat === '' || roomInfo.lng === '0' || roomInfo.lat === '0') {
      return toast(toastMessage('위치 지정 후 등록바랍니다.'), { type: 'error' });
    }
    // Open Modal
    setRoomInfo((prev) => {
      return {
        ...prev,
        isOpen: true,
      };
    });
  };

  const onUpdate = () => {
    // Open Modal Result
    setRoomInfo((prev) => {
      return {
        ...prev,
        isUpdate: true,
      };
    });
  };

  const diconnectCase = () => {
    // Open Modal Result
    setRoomInfo((prev) => {
      return {
        ...prev,
        isClose: true,
      };
    });
  };

  // 외부 뷰어 링크 (/viewer/:room/:key) 발급 후 클립보드 복사
  const copyViewerLink = async () => {
    if (roomId === undefined) {
      return toast(toastMessage('사건 선택 후 이용바랍니다.'), { type: 'error' });
    }
    try {
      const response: any = await getViewerKey(roomId, userInfo.userIdx);
      if (response.result !== 'success' || !response.viewerKey) {
        return toast(toastMessage(response.error_msg || '외부 링크 발급에 실패했습니다.'), { type: 'error' });
      }
      const url = `${window.location.origin}${process.env.PUBLIC_URL}/viewer/${roomId}/${response.viewerKey}`;
      await navigator.clipboard.writeText(url);
      toast(toastMessage('외부 링크가 복사되었습니다.'), { type: 'success' });
    } catch (error) {
      console.log('copyViewerLink Error ', error);
      toast(toastMessage('외부 링크 복사에 실패했습니다.'), { type: 'error' });
    }
  };

  // Draw for Measure
  const mapDraw = useRef<Draw>();
  const mapSource = useRef<VectorSource>(new VectorSource());
  const drawVector = new Vector({
    className: 'Measure_Base_Layer',
    source: mapSource.current,
    style: new Style({
      stroke: new Stroke({ color: '#0086ff', width: 2 }),
    }),
  });
  let sketch; //라인스트링 이벤트 시 geometry객체를 담을 변수
  let measureTooltipElement; //draw 이벤트가 진행 중일 때 담을 거리 값 element
  let measureTooltip; //툴팁 위치

  const addInteraction = () => {
    mapDraw.current = new Draw({
      source: mapSource.current,
      type: 'LineString',
    });
    mapRef.current?.addInteraction(mapDraw.current);
    createMeasureTooltip();

    mapDraw.current.on('drawstart', function (evt: any) {
      // console.log(evt);
      sketch = evt.feature;
      let tooltipCoord = evt.coordinate;

      sketch.getGeometry().on('change', function (evt) {
        const geom = evt.target;
        const output = formatLength(geom);
        tooltipCoord = geom.getLastCoordinate();

        measureTooltipElement.innerHTML = output;
        measureTooltip.setPosition(tooltipCoord);
      });
    });

    mapDraw.current.on('drawend', function (evt: any) {
      const closer = document.createElement('a');
      closer.className = 'ol-popup-closer';
      closer.id = getUid(evt.feature);
      measureTooltipElement.appendChild(closer);
      closer.addEventListener(
        'click',
        function (evt: any) {
          const overlays = [...mapRef.current!.getOverlays().getArray()];
          overlays.forEach((overlay) => {
            if (overlay.getOptions().className === 'Measure_' + evt.target.parentElement.id) {
              mapRef.current?.removeOverlay(overlay);
            }
          });
          mapRef
            .current!.getLayers()
            .getArray()
            .slice()
            .forEach((layer: any) => {
              if (layer && layer.getClassName() === 'Measure_Base_Layer') {
                layer
                  .getSource()
                  .getFeatures()
                  .forEach((feature: any) => {
                    if (getUid(feature) === evt.target.id) {
                      layer.getSource().removeFeature(feature);
                    }
                  });
              }
            });
          evt.preventDefault();
        },
        false
      );
      measureTooltipElement.className = 'ol-tooptip ol-tooltip-static';
      measureTooltip.setOffset([0, -7]);
      sketch = null;
      measureTooltipElement = null;
      if (mapRef.current !== undefined) {
        mapRef.current.removeInteraction(mapDraw.current!);
      }
    });
  };

  function createMeasureTooltip() {
    if (measureTooltipElement) {
      measureTooltipElement.parentNode.removeChild(measureTooltipElement);
    }
    measureTooltipElement = document.createElement('div');
    measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
    measureTooltipElement.id = getUid(mapDraw.current);
    measureTooltip = new Overlay({
      id: 'Measure_' + getUid(mapDraw.current),
      className: 'Measure_' + getUid(mapDraw.current),
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false,
    });
    mapRef.current?.addOverlay(measureTooltip);
  }

  function formatLength(line) {
    const length = getLength(line);
    // let output;
    // if (length > 100) {
    //   output = Math.round((length / 1000) * 100 * 0.539957) / 100 + ' ' + 'km';
    // } else {
    //   output = Math.round(length * 100) / 100 + ' ' + 'm';
    // }
    const output = Math.round((length / 1000) * 100 * 0.539957) / 100 + ' ' + '해리';
    return output;
  }

  const [isPlaying, setIsPlaying] = useState(true);
  const [playData, setPlayData] = useState<any>(undefined);

  const onSetIndex = (index: number, time: string) => {
    displayRoute(locations, index, time);
  };

  const onCloseProgress = () => {
    setIsPlaying(false);
  };

  const getFeatureStyle = (data: any) => {
    return [
      new Style({
        image: new Icon({
          src: require('../../../assets/images/icon_fishing.png'),
          color: data.role === 'V' ? '#e63528' : '#0086ff',
          rotation: isNaN(data.heading) ? undefined : data.heading * 0.01745329251 /* Degree to Radian */,
          scale: 0.8,
        }),
        zIndex: 3,
      }),
    ];
  };

  const setRoutePositiion = (arrays: any) => {
    // console.log('setRoutePositiion ', arrays);
    const extent = createEmpty();
    // Remove All Overlay & Layer
    // if (mapRef.current !== undefined) {
    //   const overlays = [...mapRef.current!.getOverlays().getArray()];
    //   overlays.forEach((overlay) => {
    //     mapRef.current?.removeOverlay(overlay);
    //   });
    //   mapRef.current
    //     .getLayers()
    //     .getArray()
    //     .slice()
    //     .forEach((layer: any) => {
    //       if (mapRef.current !== undefined && !layer.getClassName().includes('Base')) {
    //         mapRef.current.removeLayer(layer);
    //       }
    //     });
    // }
    arrays?.map((item) => {
      // Create Layer
      const gpxDrawLayer = new Vector({
        className: 'Route_' + item.phone,
        source: new VectorSource({ features: [] }),
        style: (feature: any) => {
          if (feature.get('type') === 'startIcon') {
            return new Style({
              image: new Icon({
                // anchor: [0.5, 0.5],
                opacity: 1,
                scale: 0.6,
                src:
                  item.role === 'V'
                    ? require('../../../assets/images/icon_location_v.png')
                    : require('../../../assets/images/icon_location_r.png'),
              }),
            });
          } else {
            return new Style({
              stroke: new Stroke({
                color: item.role === 'V' ? 'rgba(255, 0, 0, 1)' : 'rgba(54, 91, 242, 1)',
                width: 2,
              }),
            });
          }
        },
        zIndex: 3,
      });
      // Add Layer
      if (mapRef.current !== undefined) {
        mapRef.current.addLayer(gpxDrawLayer);
      }

      const location = item.loc;
      let coords = [] as any;
      if (location.length > 0) {
        for (let i = 0; i < location.length; i++) {
          if (location[i].lng !== 0 && location[i].lat !== 0) {
            coords = fromLonLat([location[i].lng, location[i].lat], 'EPSG:5179');
            break;
          }
        }
      }
      if (coords.length === 0) {
        // Empty GPS Data
        return;
      }
      // Add Feature Start
      if (gpxDrawLayer !== null) {
        const startIconFeature = new Feature({
          id: 'startIcon',
          name: 'start',
          type: 'startIcon',
          geometry: new Point(coords),
          data: {
            name: location.name,
            phone: location.mdn,
            rtrType: location.rtrType,
            location: location[0],
          },
        });
        gpxDrawLayer.getSource()?.addFeature(startIconFeature);

        // const feature = new Feature({
        //   id: 'route',
        //   type: 'route',
        //   geometry: new Point(coords),
        // });
        // feature.setStyle(getFeatureStyle({ role: item.role, speed: location[0].speed, heading: location[0].heading }));
        // gpxDrawLayer.getSource()?.addFeature(feature);
      }
      if (location.length > 1) {
        let coords = undefined as any;
        for (let i = 0; i < location.length; i++) {
          if (location[i].lng === 0 || location[i].lat === 0) {
            continue;
          }
          gpxDrawLayer.getSource()?.addFeature(
            new Feature({
              id: 'line',
              data: {
                name: location.name,
                phone: location.mdn,
                rtrType: location.rtrType,
                location: location[i],
              },
              geometry: new LineString(
                coords === undefined
                  ? fromLonLat([location[i].lng, location[i].lat], 'EPSG:5179').concat(
                      fromLonLat([location[i].lng, location[i].lat], 'EPSG:5179')
                    )
                  : coords.concat(fromLonLat([location[i].lng, location[i].lat], 'EPSG:5179')),
                'XY'
              ),
            })
          );
          coords = fromLonLat([location[i].lng, location[i].lat], 'EPSG:5179');
        }
        // addLocationOverlay('feature', item.phone, item.role, location[location.length - 1]);
      }
    });
    // Set fit selected vessel
    if (mapRef.current !== undefined) {
      const extent = createEmpty();
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (
            mapRef.current !== undefined &&
            !layer.getClassName().includes('Base') &&
            !layer.getClassName().includes('Predic_')
          ) {
            const source = layer.getSource();
            extend(extent, source.getExtent());
          }
        });
      if (!isEmpty(extent)) {
        mapRef.current.getView().cancelAnimations();
        mapRef.current.getView().fit(extent, {
          size: mapRef.current.getSize(),
          padding: [200, 100, 200, 100],
          maxZoom: 7,
          duration: 2000,
        });
      }
    }
  };

  const displayRoute = (arrays: any, index: number, time: string) => {
    // console.log('displayRoute', arrays, index, time);
    arrays?.map((item) => {
      const datas = item.loc;
      if (datas === undefined) {
        return;
      }
      const currentPoint = datas.find((data) => data.saveTime === time);
      if (currentPoint !== undefined) {
        if (mapRef.current !== undefined) {
          const overlay = mapRef.current.getOverlayById('route_overlay_' + item.phone);
          if (!overlay) {
            // 3. Create Feature
            mapRef.current
              .getLayers()
              .getArray()
              .filter((layer: any) => layer.getClassName() === 'Route_' + item.phone)
              .forEach((layer: any) => {
                if (mapRef.current !== undefined && layer && layer.getClassName() === 'Route_' + item.phone) {
                  const feature = new Feature({
                    id: 'position',
                    type: 'position',
                    data: { phone: item.phone, location: currentPoint },
                    geometry: new Point(fromLonLat([currentPoint.lng, currentPoint.lat], 'EPSG:5179')),
                  });
                  feature.setStyle(
                    getFeatureStyle({ role: item.role, speed: currentPoint.speed, heading: currentPoint.heading })
                  );
                  layer.getSource().addFeature(feature);
                }
              });
            // 4. Add Overlay
            const container = document.createElement('div');
            container.innerHTML = ReactDOMServer.renderToStaticMarkup(
              <MapOverlay
                users={users}
                phone={item.phone}
                role={item.role}
                location={currentPoint}
                locations={locations}
              />
            );
            const overlay = new Overlay({
              id: 'route_overlay_' + item.phone,
              element: container,
              position: fromLonLat([currentPoint.lng, currentPoint.lat], 'EPSG:5179'),
              positioning: 'bottom-center',
              offset: [0, -40],
            });
            container.addEventListener('click', function (e: Event) {
              const target = e.target as HTMLDivElement;
              if (target.className.includes('overlay-close')) {
                onCloseOverlay(item.phone);
              } else if (target.className == 'overlay-cctv') {
                // console.log('Overlay cctv : ', target.className);
              }
            });
            mapRef.current.addOverlay(overlay);
            overlay.setMap(null);
          } else {
            // 5. Update Feature
            mapRef.current
              .getLayers()
              .getArray()
              .filter((layer: any) => layer.getClassName() === 'Route_' + item.phone)
              .forEach((layer: any) => {
                if (mapRef.current !== undefined && layer && layer.getClassName() === 'Route_' + item.phone) {
                  const features = layer.getSource().getFeatures();
                  if (features != null && features.length > 0) {
                    for (const x in features) {
                      const properties = features[x].getProperties();
                      const id = properties.id;
                      if (id == 'position') {
                        features[x].set('data', { phone: item.phone, location: currentPoint });
                        features[x].setStyle(
                          getFeatureStyle({
                            role: item.role,
                            speed: currentPoint.speed,
                            heading: currentPoint.heading,
                          })
                        );
                        features[x].setGeometry(
                          new Point(fromLonLat([currentPoint.lng, currentPoint.lat], 'EPSG:5179'))
                        );
                        break;
                      }
                    }
                  }
                }
              });
            // 6. Update Overlay
            const element = overlay.getElement();
            if (element !== undefined) {
              element.innerHTML = ReactDOMServer.renderToStaticMarkup(
                <MapOverlay
                  users={users}
                  phone={item.phone}
                  role={item.role}
                  location={currentPoint}
                  locations={locations}
                />
              );
              overlay.setElement(element);
            }
            overlay.setPosition(fromLonLat([currentPoint.lng, currentPoint.lat], 'EPSG:5179'));
          }
        }
      }
    });
  };

  const onCloseModal = async (
    isRegister: boolean,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    loc: string,
    dtl: string
  ) => {
    if (isRegister) {
      // Register or Update Case
      const isSuccess = await onRegisterCase(
        roomInfo.date,
        roomInfo.time,
        roomInfo.lat,
        roomInfo.lng,
        loc,
        dtl,
        undefined,
        dLoc,
        wCond,
        accType,
        dType,
        dRoute,
        roomInfo.mdn,
        roomInfo.alimType
      );

      if (isSuccess) {
        setRoomInfo((prev) => ({
          ...prev,
          loc,
          dtl,
          dLoc,
          wCond,
          accType,
          dType,
          dRoute,
          isOpen: false,
        }));
      }
    } else {
      // close modal
      setRoomInfo((prev) => {
        return {
          ...prev,
          isOpen: false,
        };
      });
    }
  };

  const onCloseUpdate = (
    isRegister: boolean,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    prcRslt: string | undefined
  ) => {
    if (isRegister) {
      // Update Case
      onRegisterCase(
        roomInfo.date,
        roomInfo.time,
        roomInfo.lat,
        roomInfo.lng,
        roomInfo.loc,
        roomInfo.dtl,
        prcRslt,
        dLoc,
        wCond,
        accType,
        dType,
        dRoute,
        roomInfo.mdn,
        roomInfo.alimType
      );
      // close modal
      setRoomInfo((prev) => {
        return {
          ...prev,
          prcRslt,
          dLoc,
          wCond,
          accType,
          dType,
          dRoute,
          isUpdate: false,
        };
      });
    } else {
      // close modal
      setRoomInfo((prev) => {
        return {
          ...prev,
          isUpdate: false,
        };
      });
    }
  };

  const onCloseResult = (
    isRegister: boolean,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    prcRslt: string | undefined
  ) => {
    if (isRegister) {
      // Register or Update Case
      onRegisterCase(
        roomInfo.date,
        roomInfo.time,
        roomInfo.lat,
        roomInfo.lng,
        roomInfo.loc,
        roomInfo.dtl,
        prcRslt,
        dLoc,
        wCond,
        accType,
        dType,
        dRoute,
        roomInfo.mdn,
        roomInfo.alimType
      );
      onDisconnect();
    }
    // close modal
    setRoomInfo((prev) => {
      return {
        ...prev,
        isClose: false,
      };
    });
  };

  return (
    <MapTemplateWrap>
      <MapContainer ref={mapElement}></MapContainer>
      {curRoom !== undefined && (
        <RoomWrap>
          <>동원장소 : {roomInfo.dLoc}</>&ensp;|&ensp;<>기상상태 : {roomInfo.wCond}</>&ensp;|&ensp;
          <>사고유형 : {roomInfo.accType}</>
          <br />
          <>동원유형 : {roomInfo.dType}</>&ensp;|&ensp;<>동원경로 : {roomInfo.dRoute}</>
          <br />
          <>사고내용 : {roomInfo.dtl}</>
        </RoomWrap>
      )}
      <MapButton onClick={changeMap}>지도</MapButton>
      <DistanceButton onClick={setMeasure}>거리</DistanceButton>
      {curRoom !== undefined && (
        <PostioneButton onClick={setPosition}>
          <img src={process.env.PUBLIC_URL + '/images/btn_position.svg'} alt="position" />
        </PostioneButton>
      )}
      {isPlaying && playData !== undefined && curRoom !== undefined && curRoom.isExpire && (
        <SliderBarWrap>
          <SliderMap data={playData} onSetIndex={onSetIndex} onClose={onCloseProgress} />
        </SliderBarWrap>
      )}
      <ModalInfo roomId={roomId} info={roomInfo} onClose={onCloseModal} />
      {roomInfo.isUpdate && <ModalUpdate info={roomInfo} onClose={onCloseUpdate} />}
      {roomInfo.isClose && <ModalResult info={roomInfo} onClose={onCloseResult} />}
      <OverviewWrap>
        <HorizontalWrap>
          <LocationlWrap>
            <MapCoordinate
              userinfo={userinfo}
              lng={roomInfo.lng}
              lat={roomInfo.lat}
              mdn={roomInfo.mdn}
              isExpire={roomId !== undefined && roomInfo.isExpire}
              onChange={onChangePosition}
            />
            <Seperator>|</Seperator>
            <PickerlWrap>
              <MapDatePicker value={roomInfo.date} onChange={onChangeDate}></MapDatePicker>
              <Seperator />
              <MapTimePicker value={roomInfo.date + ' ' + roomInfo.time} onChange={onChangeTime}></MapTimePicker>
              {!(curRoom !== undefined && curRoom.isExpire) && (
                <InputButton onClick={updateCase} color={roomId}>
                  {roomId === undefined ? '해양사고 등록' : '사고정보 수정'}
                </InputButton>
              )}
            </PickerlWrap>
          </LocationlWrap>
        </HorizontalWrap>
        {roomId !== undefined && (
          <HorizontalWrap>
            <ControlButton onClick={onInitialize}>초기화</ControlButton>
            {curRoom !== undefined && !curRoom.isExpire && (
              <ControlButton
                disabled={roomId === undefined ? true : false}
                onClick={() => {
                  onMute(!muted);
                  setMuted(!muted);
                }}
              >
                {muted ? (
                  <img src={process.env.PUBLIC_URL + '/images/icon_audio_muted.svg'} alt="audio" />
                ) : (
                  <img src={process.env.PUBLIC_URL + '/images/icon_audio.svg'} alt="audio muted" />
                )}
              </ControlButton>
            )}
            {curRoom !== undefined && !curRoom.isExpire && (
              <>
                <ControlButton onClick={onUpdate} disabled={roomId === undefined ? true : false}>
                  구조 활동
                </ControlButton>
                <ControlButton onClick={diconnectCase} disabled={roomId === undefined ? true : false}>
                  진행 종료
                </ControlButton>
              </>
            )}
          </HorizontalWrap>
        )}
      </OverviewWrap>
    </MapTemplateWrap>
  );
};

const MapTemplateWrap = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;
const MapContainer = styled.div`
  width: 100%;
  height: calc(100% - 6.375rem);
  border: 1px solid #000000;

  .ol-tooltip {
    position: relative;
    display: block;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.9;
    white-space: nowrap;
    font-size: 14px;
    cursor: default;
    user-select: none;
  }
  .ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
  }
  .ol-tooltip-static {
    background-color: #0086ff;
    color: white;
    padding: 4px 8px;
    border: 1px solid darkgray;
    border-radius: 4px;
  }
  .ol-tooltip-measure:before,
  .ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: '';
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
  }
  .ol-tooltip-static:before {
    border-top-color: #0086ff;
  }
  .ol-popup-closer:after {
    margin-left: 10px;
    color: black;
    content: '✖';
    cursor: pointer;
  }
`;
const RoomWrap = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  max-width: 90%;
  padding: 0.75rem 1rem;

  min-height: 90px;
  height: auto;

  word-break: break-all;
  white-space: pre-wrap;

  border-radius: 0.3125rem;
  border: none;
  background: rgba(45, 66, 84, 0.8);
  font-family: 'Noto Sans CJK KR';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 25px;
  color: #ffffff;
  cursor: pointer;
  z-index: 1;

  max-height: 300px;
  overflow-y: auto;
`;
const MapButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 2.8125rem;
  height: 2.25rem;
  border-radius: 0.3125rem;
  border: none;
  background: #0069d0;
  font-family: 'Noto Sans CJK KR';
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  cursor: pointer;
  z-index: 1;
`;
const DistanceButton = styled.button`
  position: absolute;
  top: 60px;
  right: 10px;
  width: 2.8125rem;
  height: 2.25rem;
  border-radius: 0.3125rem;
  border: none;
  background: #00704f;
  font-family: 'Noto Sans CJK KR';
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  cursor: pointer;
  z-index: 1;
`;
const PostioneButton = styled.button`
  position: absolute;
  bottom: 110px;
  right: 10px;
  border-radius: 0.3125rem;
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 1;
`;
const SliderBarWrap = styled.div`
  position: absolute;
  width: 100%;
  bottom: 30px;
`;
const OverviewWrap = styled.div`
  width: 100%;
  height: 6.375rem;
  background: radial-gradient(50% 50% at 50% 50%, #00315c 0%, #000 100%);
`;
const HorizontalWrap = styled.div`
  height: 50%;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
`;
const LocationlWrap = styled.div`
  width: 95%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const InputButton = styled.button`
  width: 105px;
  height: 30px;
  margin-left: 5px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  color: #ffffff;
  background: ${(props) => (props.color === undefined ? '#0086FF' : '#668198')};
  border-radius: 0.3125rem;
  border: none;
  cursor: pointer;
`;
const Seperator = styled.div`
  width: 5px;
  height: 19px;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
`;
const PickerlWrap = styled.div`
  display: flex;
  margin: auto;
`;
const ControlButton = styled.button`
  width: 11.25rem;
  height: 2.4375rem;
  margin-left: 20px;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #ffffff;
  background: radial-gradient(50% 50% at 50% 50%, #5c0600 0%, #000 100%);
  border-radius: 0.625rem;
  border: 2px solid #cf1900;
  cursor: pointer;
`;

export default CenterMap;
